/**
 * Build the URL for navigating from a calendar view to the legacy (soon React)
 * Day View for a specific date.
 *
 * Pattern verified against the legacy templates that still ship the working
 * Day View navigation:
 *   - `views/calendar/calendar-month-legacy.twig:98` — `toLink('{{ _org }}/calendar/day/{YYYY-MM-DD}/{timezone}/')`
 *   - `views/calendar/calendar-week.twig:182` — `href="{{ _org }}/calendar/day/{{ date|date('Y-m-d') }}/{{ _timezone ? _timezone ~ '/' : '' }}"`
 *
 * `_org` resolves to `/org/{orgUnique}`. Timezone is the optional trailing
 * segment — both templates omit it when empty, so we follow the same rule and
 * only emit it when the caller passes a non-empty `timezone` string.
 *
 * When the Day View finishes its own React port (Phase 3), the caller should
 * switch from `window.location.href =` to a React Router `navigate()` and
 * this helper can either return the same path or be updated to emit a hash
 * fragment instead — the shape stays backwards-compatible.
 *
 * @param {{orgUnique: string, date: string|Date, timezone?: string|null}} params
 *   `date` is either a `YYYY-MM-DD` string (already formatted by the caller)
 *   or a `Date` instance that will be formatted to UTC-independent
 *   `YYYY-MM-DD` via the caller's own isoDate helper — this helper does NOT
 *   import anything from `calendar-month/` to keep the `calendar-common`
 *   directory free of month-specific deps.
 * @returns {string|null}  Absolute path, or `null` when `orgUnique`/`date`
 *   are missing (caller should no-op the navigation in that case).
 */
export function buildDayViewUrl({ orgUnique, date, timezone } = {}) {
	if (!orgUnique || !date) return null;
	const iso = typeof date === 'string' ? date : formatIsoDateLocal(date);
	if (!iso) return null;
	const tzSegment =
    typeof timezone === 'string' && timezone.trim() !== ''
      ? `${timezone}/`
      : '';
	return `/org/${encodeURIComponent(orgUnique)}/calendar/day/${iso}/${tzSegment}`;
}

/**
 * Format a `Date` instance to a local-time `YYYY-MM-DD` string. Inlined here
 * (rather than imported from `calendar-month/utils/urlPathSync`) so the
 * `calendar-common/navigation` module can stay dep-free and be reused by
 * Week/Day containers without pulling a month-specific utility into them.
 *
 * @param {Date} date
 * @returns {string|null}
 */
function formatIsoDateLocal(date) {
	if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
	const yyyy = String(date.getFullYear()).padStart(4, '0');
	const mm = String(date.getMonth() + 1).padStart(2, '0');
	const dd = String(date.getDate()).padStart(2, '0');
	return `${yyyy}-${mm}-${dd}`;
}
